import { useCallback, useEffect, useState } from 'react';

const subscribers = new Set();
let deferredPromptEvent = null;
let isInstalledStandalone = false;
let listenersInitialized = false;

const isWindowDefined = () => typeof window !== 'undefined';

const getSnapshot = () => ({
  canInstall: Boolean(deferredPromptEvent),
  isInstalled: isInstalledStandalone,
});

const notifySubscribers = () => {
  const snapshot = getSnapshot();
  subscribers.forEach((listener) => listener(snapshot));
};

const setupGlobalListeners = () => {
  if (!isWindowDefined() || listenersInitialized) {
    return;
  }
  listenersInitialized = true;

  const updateInstalledState = () => {
    if (!isWindowDefined()) {
      return;
    }
    const displayModeMatches =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches;
    const iosStandalone = Boolean(window.navigator?.standalone);
    isInstalledStandalone = Boolean(displayModeMatches || iosStandalone);
    notifySubscribers();
  };

  const handleBeforeInstallPrompt = (event) => {
    event.preventDefault();
    deferredPromptEvent = event;
    notifySubscribers();
  };

  const handleAppInstalled = () => {
    deferredPromptEvent = null;
    isInstalledStandalone = true;
    notifySubscribers();
  };

  if (typeof window.matchMedia === 'function') {
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleMediaQueryChange = (event) => {
      isInstalledStandalone = event.matches || Boolean(window.navigator?.standalone);
      notifySubscribers();
    };
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleMediaQueryChange);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleMediaQueryChange);
    }
    // Sync initial state
    isInstalledStandalone = mediaQuery.matches || Boolean(window.navigator?.standalone);
  } else {
    isInstalledStandalone = Boolean(window.navigator?.standalone);
  }

  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  window.addEventListener('appinstalled', handleAppInstalled);
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      updateInstalledState();
    }
  });

  updateInstalledState();
};

export function usePwaInstaller() {
  const [snapshot, setSnapshot] = useState(() => getSnapshot());
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    setupGlobalListeners();
    const listener = (nextSnapshot) => setSnapshot(nextSnapshot);
    subscribers.add(listener);
    // Ensure the subscriber sees the latest snapshot immediately.
    listener(getSnapshot());
    return () => {
      subscribers.delete(listener);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferredPromptEvent) {
      return 'unavailable';
    }

    setIsInstalling(true);
    try {
      deferredPromptEvent.prompt();
      const result = await deferredPromptEvent.userChoice;
      deferredPromptEvent = null;
      notifySubscribers();
      return result?.outcome === 'accepted' ? 'accepted' : 'dismissed';
    } finally {
      setIsInstalling(false);
    }
  }, []);

  return {
    canInstall: snapshot.canInstall,
    isInstalled: snapshot.isInstalled,
    isInstalling,
    install,
  };
}
